import { Analytics } from '@cobliteam/analytics';
import type { Insight } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useInsights } from '@cobliteam/hooks';
import Button from '@cobliteam/ui/components/Button';
import Divider from '@cobliteam/ui/components/Divider';
import Typography from '@cobliteam/ui/components/Typography';
import Textarea from '@cobliteam/ui/form/Textarea';
import Icon from '@cobliteam/ui/icons';
import { subWeeks } from '@cobliteam/utils/date';
import { useEffect, useState } from 'react';
import insightsUtils from '../../../utils';

export interface InsightContentProps {
  insight: Insight;
  isFirstInsight: boolean;
  isLastInsight: boolean;
  onPreviousInsightClick: () => void;
  onNextInsightClick: () => void;
}

type FeedbackOption = 'thumbs-up' | 'thumbs-down';

const InsightContent = ({ insight, isFirstInsight, isLastInsight, onPreviousInsightClick, onNextInsightClick }: InsightContentProps) => {
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackOption | undefined>(undefined);
  const [negativeFeedbackMessage, setNegativeFeedbackMessage] = useState('');
  const [showSuccessFeedbackMessage, setShowSuccessFeedbackMessage] = useState(false);

  const { getInsightType } = insightsUtils();
  const { useEvaluateInsightThumbsDown, useEvaluateInsightThumbsUp } = useInsights;
  const { mutateAsync: sendThumbsUp } = useEvaluateInsightThumbsUp({ insightId: insight.id });
  const { mutateAsync: sendThumbsDown } = useEvaluateInsightThumbsDown({ insightId: insight.id, comment: negativeFeedbackMessage });

  useEffect(() => {
    setSelectedFeedback(undefined);
    setNegativeFeedbackMessage('');
    setShowSuccessFeedbackMessage(false);
  }, [insight]);

  const onSafetyReportyClick = () => {
    const queryString = new URLSearchParams();

    // vehicle
    // temporary getting vehicleId from text while backend has invalid fields
    const vehicleRegex = /VehicleID\s*=\s*([0-9a-fA-F-]{36})/;
    const vehicleMatch = insight.insightText?.match(vehicleRegex);

    if (vehicleMatch) {
      queryString.append('vehicleIds', vehicleMatch[1]);
    } else if (insight.vehicle) {
      queryString.append('vehicleIds', insight.vehicle.id);
    }

    // driver

    // temporary getting driverId from text while backend has invalid fields
    const driverRegex = /DriverID\s*=\s*([0-9a-fA-F-]{36})/;
    const driverMatch = insight.insightText?.match(driverRegex);

    if (driverMatch) {
      queryString.append('driverIds', driverMatch[1]);
    } else if (insight.driver) {
      queryString.append('driverIds', insight.driver.id);
    }

    Analytics.track('Insight Modal Safety Report CTA Clicked');

    const reportPageView = driverMatch || insight.driver ? 'driver' : 'vehicle';

    queryString.append('endDate', insight.createdAt);
    queryString.append('startDate', subWeeks(new Date(insight.createdAt), 1).toString());

    window.open(`https://painel.cobli.co/report/safety/${reportPageView}?page=0&size=10&${queryString.toString()}`, '_blank');
  };

  const handleFeedbackClick = async (feedback: FeedbackOption) => {
    if (selectedFeedback !== undefined) return;
    setSelectedFeedback(feedback);
    if (feedback === 'thumbs-up') {
      await sendThumbsUp();
      setShowSuccessFeedbackMessage(true);
      Analytics.track('Insight Modal Feedback Clicked', { id: insight.id, type: getInsightType(insight), category: insight.category, feedbackType: 'positive' });
    }
  };

  const handleNegativeFeedbackSubmit = async () => {
    await sendThumbsDown();
    setShowSuccessFeedbackMessage(true);
    Analytics.track('Insight Modal Feedback Clicked', {
      id: insight.id,
      type: getInsightType(insight),
      category: insight.category,
      feedbackType: 'negative',
      comment: negativeFeedbackMessage,
    });
  };

  const renderPositiveFeedbackMessage = () => (
    <div className='flex flex-col gap-2'>
      <Typography variant='caption-bold' className='text-gray-11'>
        Obrigado pela sua resposta!
      </Typography>
      <Typography className='text-gray-11' variant='text-sm'>
        Ela será utilizada para aprimorar os próximos insights.
      </Typography>
    </div>
  );

  const renderNegativeFeedbackMessage = () => (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-2'>
        <Typography variant='text-md-semibold'>Como este insight poderia ter sido mais valioso para a sua gestão?</Typography>
        <Textarea className='w-full' placeholder='Escrever sugestão.' onChange={(event) => setNegativeFeedbackMessage(event.target.value)} />
      </div>
      <Button variant='contained' color='primary' size='sm' className='self-end' disabled={negativeFeedbackMessage.length === 0} onClick={handleNegativeFeedbackSubmit}>
        Enviar resposta
      </Button>
    </div>
  );

  return (
    <div className='flex h-full flex-col overflow-y-visible'>
      <div className='mt-6 mb-auto flex flex-col gap-6'>
        <div>
          <Typography className='mb-2' variant='text-lg-semibold' color='secondary'>
            Insight e impacto
          </Typography>
          <Typography variant='text-md'>{insight.impact}</Typography>
        </div>
        <div>
          <Typography className='mb-2' variant='text-lg-semibold' color='secondary'>
            Ação Recomendada
          </Typography>
          <Typography variant='text-md'>{insight.action}</Typography>
        </div>
        <Button onClick={onSafetyReportyClick} startIcon={<Icon name='external-link' />} variant='ghost' color='primary' className='mt-6 self-start'>
          Conferir no relatório de segurança
        </Button>

        <div className='mb-6 flex flex-col gap-2 rounded-sm bg-gray-2 px-3 py-2'>
          <div className='flex flex-row items-center gap-4'>
            <Typography className='text-gray-11' variant='text-md'>
              Este insight foi útil para você?
            </Typography>
            <div className='flex flex-row gap-2'>
              <div onClick={() => handleFeedbackClick('thumbs-up')}>
                <Icon className='cursor-pointer' color={selectedFeedback === 'thumbs-up' ? 'success' : undefined} name='thumbs-up' />
              </div>
              <div onClick={() => handleFeedbackClick('thumbs-down')}>
                <Icon className='cursor-pointer' color={selectedFeedback === 'thumbs-down' ? 'danger' : undefined} name='thumbs-down' />
              </div>
            </div>
          </div>
          {!showSuccessFeedbackMessage && selectedFeedback === 'thumbs-down' && renderNegativeFeedbackMessage()}
          {showSuccessFeedbackMessage && renderPositiveFeedbackMessage()}
        </div>
      </div>

      <div className='flex flex-col gap-6'>
        <Divider className='mt-auto' />
        <div className='flex flex-row justify-between'>
          {!isFirstInsight && (
            <Button startIcon={<Icon name='arrow-left' />} variant='ghost' onClick={onPreviousInsightClick}>
              Insight anterior
            </Button>
          )}
          {!isLastInsight && (
            <Button variant='outlined' color='primary' endIcon={<Icon name='arrow-right' />} className='ml-auto' onClick={onNextInsightClick}>
              Próximo insight
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsightContent;
